import api from './api';
import logger from '../utils/logger';
import { deduplicateRequest } from '../utils/debounce';

// Simple in-memory cache for telemetry data
const telemetryCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL_MS = 30000; // 30 seconds

export interface TelemetryData {
  id: string;
  deviceId: string;
  orgId: string;
  ts: string;
  temperatureC: number | null;
  humidityRh: number | null;
  levelCm: number | null;
  batteryV: number | null;
  rssiDbm: number | null;
}

export interface HourlyTelemetryData {
  deviceId: string;
  orgId: string;
  hourTs: string;
  avgTempC: number | null;
  minTempC: number | null;
  maxTempC: number | null;
  avgHumRh: number | null;
  minHumRh: number | null;
  maxHumRh: number | null;
  avgLevelCm?: number | null;
  minLevelCm?: number | null;
  maxLevelCm?: number | null;
  avgBatteryV: number | null;
  minBatteryV: number | null;
  avgRssiDbm: number | null;
  sampleCount: number;
  firstTs: string;
  lastTs: string;
}

export interface TelemetryRangeResponse {
  data: TelemetryData[];
  availableMetrics: string[];
  timeRange: {
    start: string;
    end: string;
  };
}

export interface HourlyTelemetryResponse {
  data: HourlyTelemetryData[];
  granularity: 'hourly' | 'raw';
  totalPoints: number;
  timeRange: {
    from: string;
    to: string;
  };
  availableMetrics: string[];
}

const telemetryApi = {
  /**
   * Convert raw telemetry data to hourly format (helper for fallback)
   */
  convertToHourlyFormat(rawData: TelemetryData[]): HourlyTelemetryData[] {
    // Group data by hour
    const hourlyMap = new Map<string, TelemetryData[]>();

    rawData.forEach((point) => {
      const hourKey = new Date(point.ts).toISOString().slice(0, 13) + ':00:00.000Z';
      if (!hourlyMap.has(hourKey)) {
        hourlyMap.set(hourKey, []);
      }
      hourlyMap.get(hourKey)!.push(point);
    });

    // Calculate aggregates for each hour
    return Array.from(hourlyMap.entries())
      .map(([hourTs, points]) => {
        const temps = points.map((p) => p.temperatureC).filter((v) => v !== null) as number[];
        const hums = points.map((p) => p.humidityRh).filter((v) => v !== null) as number[];
        const levels = points.map((p) => p.levelCm).filter((v) => v !== null) as number[];
        const batteries = points.map((p) => p.batteryV).filter((v) => v !== null) as number[];
        const rssis = points.map((p) => p.rssiDbm).filter((v) => v !== null) as number[];

        return {
          deviceId: points[0].deviceId,
          orgId: points[0].orgId,
          hourTs,
          avgTempC: temps.length > 0 ? temps.reduce((a, b) => a + b, 0) / temps.length : null,
          minTempC: temps.length > 0 ? Math.min(...temps) : null,
          maxTempC: temps.length > 0 ? Math.max(...temps) : null,
          avgHumRh: hums.length > 0 ? hums.reduce((a, b) => a + b, 0) / hums.length : null,
          minHumRh: hums.length > 0 ? Math.min(...hums) : null,
          maxHumRh: hums.length > 0 ? Math.max(...hums) : null,
          avgLevelCm: levels.length > 0 ? levels.reduce((a, b) => a + b, 0) / levels.length : null,
          minLevelCm: levels.length > 0 ? Math.min(...levels) : null,
          maxLevelCm: levels.length > 0 ? Math.max(...levels) : null,
          avgBatteryV:
            batteries.length > 0 ? batteries.reduce((a, b) => a + b, 0) / batteries.length : null,
          minBatteryV: batteries.length > 0 ? Math.min(...batteries) : null,
          avgRssiDbm: rssis.length > 0 ? rssis.reduce((a, b) => a + b, 0) / rssis.length : null,
          sampleCount: points.length,
          firstTs: points[0].ts,
          lastTs: points[points.length - 1].ts,
        };
      })
      .sort((a, b) => new Date(a.hourTs).getTime() - new Date(b.hourTs).getTime());
  },

  /**
   * Get hourly aggregated telemetry (RECOMMENDED - fast & compliance-friendly)
   */
  async getHourly(
    orgId: string,
    deviceId: string,
    from: string, // ISO date string
    to: string, // ISO date string
  ): Promise<HourlyTelemetryResponse> {
    const cacheKey = `hourly_${deviceId}_${from}_${to}`;

    // Check in-memory cache first (30s TTL)
    const cached = telemetryCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      logger.log('📊 Returning cached telemetry for', deviceId);
      return cached.data;
    }

    try {
      logger.log('📊 Fetching hourly telemetry:', { orgId, deviceId, from, to });

      const response = await deduplicateRequest(
        cacheKey,
        () =>
          api.get(`/telemetry/${orgId}/${deviceId}/hourly`, {
            params: { from, to },
            timeout: 8000, // 8s (reduced from 10s)
          }),
        30000, // 30s dedup window (increased from 2s)
      );

      const result: HourlyTelemetryResponse = response.data || {
        data: [],
        granularity: 'hourly',
        totalPoints: 0,
        timeRange: { from, to },
        availableMetrics: [],
      };

      logger.log('📊 Telemetry response:', {
        dataCount: result.data?.length || 0,
        metrics: result.availableMetrics || [],
      });

      // Store in cache
      telemetryCache.set(cacheKey, { data: result, timestamp: Date.now() });

      return result;
    } catch (error: any) {
      logger.error('Failed to fetch hourly telemetry:', {
        error: error.message,
        status: error.response?.status,
      });

      // Return empty data instead of throwing
      return {
        data: [],
        granularity: 'hourly',
        totalPoints: 0,
        timeRange: { from, to },
        availableMetrics: [],
      };
    }
  },

  /**
   * @deprecated Use {@link getHourly} instead — getRange() calls the raw
   * scan endpoint which returns up to 5,000 rows per request and bypasses
   * the pre-aggregated telemetry_hourly table.
   *
   * @see getHourly
   */
  async getRange(
    orgId: string,
    deviceId: string,
    timeRange?: string,
    startDate?: string,
    endDate?: string,
  ): Promise<TelemetryRangeResponse> {
    try {
      const params: Record<string, string> = {};
      if (timeRange) {
        params.timeRange = timeRange;
      } else if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      logger.log('📊 Fetching telemetry range (DEPRECATED):', {
        orgId,
        deviceId,
        timeRange,
        params,
      });

      const response = await api.get(`/devices/${deviceId}/telemetry`, {
        params: {
          startTime: params.startDate,
          endTime: params.endDate,
          limit: 1000,
        },
        timeout: 10000, // 10s timeout
      });

      logger.log('📊 Telemetry response:', {
        dataCount: response.data?.data?.length || 0,
        metrics: response.data?.availableMetrics || [],
        status: response.status,
      });

      // Return empty data if no data available
      if (!response.data || !response.data.data) {
        return {
          data: [],
          availableMetrics: [],
          timeRange: { start: '', end: '' },
        };
      }

      return response.data;
    } catch (error: any) {
      logger.error('Failed to fetch telemetry range:', {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });

      // Return empty data instead of throwing
      return {
        data: [],
        availableMetrics: [],
        timeRange: { start: '', end: '' },
      };
    }
  },

  async getLatest(orgId: string, deviceId: string): Promise<TelemetryData | null> {
    try {
      const response = await api.get(`/devices/${deviceId}/telemetry/latest`, {
        timeout: 5000, // 5s — latest reading should be fast
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404 || error.code === 'ECONNABORTED') {
        logger.warn('No latest telemetry or timeout:', error.message);
        return null;
      }
      logger.error('Failed to fetch latest telemetry:', error);
      return null; // Return null instead of throwing
    }
  },

  /**
   * Clear cached telemetry data for a device.
   */
  async clearCache(deviceId: string): Promise<void> {
    for (const key of telemetryCache.keys()) {
      if (key.includes(deviceId)) {
        telemetryCache.delete(key);
      }
    }
  },
};

export default telemetryApi;
